/**
 * Voluum save path for useFlowDraft — extracted as-is from the original
 * draft-save body so behaviour for Voluum campaigns stays byte-identical.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  useToggleFlowPathStatus,
  useUpdateFlowItemWeights,
  useUpdateFlowPathWeights,
} from '@/hooks';

import type { DraftPath } from '../utils/flowDraft.utils';

export interface SaveVoluumPerItemParams {
  flowId: string;
  draftPaths: DraftPath[];
  togglePathMutation: ReturnType<typeof useToggleFlowPathStatus>;
  updateItemWeightsMutation: ReturnType<typeof useUpdateFlowItemWeights>;
  updatePathWeightsMutation: ReturnType<typeof useUpdateFlowPathWeights>;
}

export async function saveVoluumPerItem(
  p: SaveVoluumPerItemParams,
): Promise<void> {
  await syncPathToggles(p);
  await syncPathWeights(p);
  await syncItemWeights(p);
}

async function syncPathToggles(p: SaveVoluumPerItemParams): Promise<void> {
  const toggles = p.draftPaths
    .filter((path) => path.active !== path.originalActive)
    .map((path) => ({ pathId: path.id, active: path.active }));
  for (const toggle of toggles) {
    await p.togglePathMutation.mutateAsync({
      flowId: p.flowId,
      pathId: toggle.pathId,
      active: toggle.active,
    });
  }
}

async function syncPathWeights(p: SaveVoluumPerItemParams): Promise<void> {
  const pathWeightUpdates = p.draftPaths
    .filter((path) => path.active && path.weight !== path.originalWeight)
    .map((path) => ({ itemId: path.id, weight: path.weight }));
  if (pathWeightUpdates.length === 0) return;
  await p.updatePathWeightsMutation.mutateAsync({
    flowId: p.flowId,
    updates: pathWeightUpdates,
  });
}

async function syncItemWeights(p: SaveVoluumPerItemParams): Promise<void> {
  for (const path of p.draftPaths) {
    const landerUpdates = path.landers
      .filter((l) => l.weight !== l.originalWeight)
      .map((l) => ({ itemId: l.id, weight: l.weight }));
    if (landerUpdates.length > 0) {
      await p.updateItemWeightsMutation.mutateAsync({
        flowId: p.flowId,
        pathId: path.id,
        type: 'landers',
        updates: landerUpdates,
      });
    }
    const offerUpdates = path.offers
      .filter((o) => o.weight !== o.originalWeight)
      .map((o) => ({ itemId: o.id, weight: o.weight }));
    if (offerUpdates.length > 0) {
      await p.updateItemWeightsMutation.mutateAsync({
        flowId: p.flowId,
        pathId: path.id,
        type: 'offers',
        updates: offerUpdates,
      });
    }
  }
}
